import type { CycleForSubject, InviteAnonymized } from "~vue/api/generated/reflections";
import type { UserDetails } from "~vue/appTypes";
import type { Ref } from "vue";
import { inject, ref } from "vue";

export function raterLink(token: string): string {
    return `${globalThis.location.origin}/reflections/360/r/${token}/`;
}

/**
 * Copy-to-clipboard behavior for a subject's 360 rater invites, shared by the
 * cycle detail and nominate pages. Returns the "just copied" invite id (so the
 * trigger can flip to a check mark) plus the two copy actions: the bare rater
 * link, or a ready-to-send first-person invite message that folds in the
 * cycle's question count and personal note.
 */
export function useInviteCopy(cycle: Ref<CycleForSubject | null>) {
    const userDetails = inject<Ref<UserDetails>>("user_details");
    const copiedInviteId = ref<string | null>(null);

    function showCopied(inviteId: string): void {
        copiedInviteId.value = inviteId;
        globalThis.setTimeout(() => {
            if (copiedInviteId.value === inviteId) {
                copiedInviteId.value = null;
            }
        }, 1500);
    }

    async function copyInviteLink(invite: InviteAnonymized): Promise<void> {
        if (!invite.invite_token) {
            return;
        }
        await navigator.clipboard.writeText(raterLink(invite.invite_token));
        showCopied(invite.id);
    }

    async function copyInviteMessage(invite: InviteAnonymized): Promise<void> {
        if (!invite.invite_token) {
            return;
        }
        const firstName = userDetails?.value?.first_name || "";
        const questionCount = cycle.value?.template.question_count ?? 0;
        const personalNote = cycle.value?.personal_note;
        let message = `Hi, I've asked you to give me 360 feedback. You'll answer ${questionCount} short question${questionCount === 1 ? "" : "s"} about how I work. Your answers are anonymous. It takes about 15 minutes.`;
        if (personalNote) {
            message += `\n\n"${personalNote}"${firstName ? ` — ${firstName}` : ""}`;
        }
        message += `\n\n${raterLink(invite.invite_token)}`;
        await navigator.clipboard.writeText(message);
        showCopied(invite.id);
    }

    return { copiedInviteId, raterLink, copyInviteLink, copyInviteMessage };
}
